import type { Logger } from './logger';
import type { MediaDevices } from './types';

// `HTMLMediaElement.setSinkId` (output-device routing) is not present in every
// TS DOM lib version, so narrow to it locally rather than relying on the ambient
// type. Browsers that lack it (Firefox, older Safari) simply can't reroute
// output; we detect and report that.
type SinkCapable = HTMLAudioElement & { setSinkId?: (deviceId: string) => Promise<void> };

/**
 * Owns the single hidden `<audio>` element that plays far-end audio, plus the
 * operator's device preferences (mic in, speaker out, volume). Kept in one place
 * so the {@link Phone} and every {@link Call} share the same sink and settings.
 */
export class MediaController {
  private audio: HTMLAudioElement | null = null;
  private inputDeviceId?: string;
  private outputDeviceId?: string;
  private volume = 1;

  constructor(private log: Logger) {}

  private element(): HTMLAudioElement {
    if (this.audio) return this.audio;
    if (typeof document === 'undefined') {
      throw new Error('voice-sdk needs a browser DOM to play audio (no `document` found).');
    }
    const el = document.createElement('audio');
    el.autoplay = true;
    el.setAttribute('playsinline', '');
    el.style.display = 'none';
    document.body.appendChild(el);
    this.audio = el;
    return el;
  }

  /** Route a remote media stream to the speaker. */
  attachStream(stream: MediaStream): void {
    const el = this.element() as SinkCapable;
    el.srcObject = stream;
    el.volume = this.volume;
    if (this.outputDeviceId && typeof el.setSinkId === 'function') {
      el.setSinkId(this.outputDeviceId).catch((e) => this.log.error('setSinkId failed', e));
    }
    // Autoplay can be deferred until a user gesture; swallow the rejection.
    void el.play?.().catch(() => undefined);
  }

  /** Stop playing far-end audio (call ended). */
  detach(): void {
    if (this.audio) this.audio.srcObject = null;
  }

  setVolume(value: number): void {
    this.volume = Math.max(0, Math.min(1, value));
    if (this.audio) this.audio.volume = this.volume;
  }

  setInputDevice(deviceId: string): void {
    // Applied to the next getUserMedia (next call / answer). Live mic swap on an
    // established call would need track replacement — a later enhancement.
    this.inputDeviceId = deviceId;
  }

  async setOutputDevice(deviceId: string): Promise<void> {
    this.outputDeviceId = deviceId;
    const el = this.element() as SinkCapable;
    if (typeof el.setSinkId !== 'function') {
      throw new Error('This browser does not support output-device selection (HTMLMediaElement.setSinkId).');
    }
    await el.setSinkId(deviceId);
  }

  /** `getUserMedia` constraints honouring the chosen microphone. */
  audioConstraints(): MediaStreamConstraints {
    return {
      audio: this.inputDeviceId ? { deviceId: { exact: this.inputDeviceId } } : true,
      video: false,
    };
  }

  async listDevices(): Promise<MediaDevices> {
    if (typeof navigator === 'undefined' || !navigator.mediaDevices) {
      throw new Error('mediaDevices API unavailable — needs a secure context (HTTPS or localhost).');
    }
    const all = await navigator.mediaDevices.enumerateDevices();
    return {
      inputs: all.filter((d) => d.kind === 'audioinput'),
      outputs: all.filter((d) => d.kind === 'audiooutput'),
    };
  }
}
